import React from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { TaskStatus } from 'constants/enums';
import { Error, Loading } from 'components/Fetch';
import { useAxios } from 'configs/requests';
import { TasksList } from 'components/UI';

interface IDeleteClinicalTaskModalProps {
  handleClose: (refresh?: boolean) => void;
  task?: ITask | null;
  carePlanTemplate?: ICarePlanTemplate;
}

interface IDeleteClinicalTaskPayload {
  status: number;
  tasks: ITask[];
}

const DeleteClinicalTaskModal = ({
  handleClose,
  task,
  carePlanTemplate
}: IDeleteClinicalTaskModalProps) => {
  const { formatMessage } = useIntl();

  const getDeleteUrl = () => {
    if (carePlanTemplate) return '/clinician/2.0.0/CarePlanTemplate';
    else if (task?.triggerId) return `/clinician-gateway/careplan-triggers/${task?.triggerId}`;
    else return '/clinician-gateway/Tasks/status';
  };

  const getDeleteMethod = () => {
    if (carePlanTemplate) return 'POST';
    else if (task?.triggerId) return 'DELETE';
    else return 'PATCH';
  };

  const getDeletePayload = () => {
    if (carePlanTemplate) {
      return {
        carePlanTemplate: carePlanTemplate,
        tasks: [{ ...task, edit_status: 'deleted' }]
      };
    } else if (task?.triggerId) {
      return null;
    } else {
      return {
        status: parseInt(TaskStatus.ARCHIVED),
        tasks: [task]
      };
    }
  };

  const [{ loading: loadingUpdateTask, error: errorUpdateTask }, updateTask] =
    useAxios<IDeleteClinicalTaskPayload>(
      {
        url: getDeleteUrl(),
        method: getDeleteMethod()
      },
      { manual: true }
    );

  const handleDelete = async () => {
    if (task) {
      await updateTask({
        data: getDeletePayload()
      });
      handleClose(true);
    }
  };

  return (
    <Modal
      show
      onHide={handleClose}
      backdrop="static"
      data-test="careplan-tasks-delete-modal"
      size="xl"
    >
      <Modal.Header className="align-items-center">
        <Modal.Title>
          {formatMessage({ id: 'patient.careplan.tasks.delete.modalTitle' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorUpdateTask && <Error retry={handleDelete} />}
        <TasksList tasks={task ? [task] : []} truncate={true} carePlanTemplate={carePlanTemplate} />
        {loadingUpdateTask && <Loading className="mt-4" />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          data-test="careplan-tasks-delete-cancel"
          variant="link"
          onClick={() => handleClose()}
        >
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button data-test="careplan-tasks-delete-submit" variant="danger" onClick={handleDelete}>
          {formatMessage({ id: 'deleteKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteClinicalTaskModal;
